import type { MutableRefObject } from 'react'
import type { IPOI, PoiListParams } from '@/shared/api/types/poi-types'
import type { ITourPoi } from '@/shared/api/types/tour-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantPoisInfiniteQuery } from '@/entities/poi/api/poi-queries'
import {
  useAddTourPoisMutation,
  useRemoveTourPoisMutation,
  useUpdateTourPoiOrderMutation,
  useUpdateTourPoisBatchMutation,
} from '@/entities/tour/api/tour-queries'
import {
  appendPois,
  buildPoiLookup,
  diffTourPois,
  findOutOfScopePoiIds,
} from '@/features/tour/tour-form-model'
import { TOUR_FORM_POI_PAGE_SIZE } from '@/shared/constants/tour-form'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'

type PoiQueryFilters = Pick<PoiListParams, 'keyword' | 'category' | 'type' | 'isActive'>

interface UseTourPoisParams {
  tenantId: string
  tenantDestinationId: string
  languageCode: string
  isEdit: boolean
  tourId: string | undefined
  selectedPois: ITourPoi[]
  updateSelectedPois: (updater: (prev: ITourPoi[]) => ITourPoi[]) => void
  /** POIs currently stored on the server (diff base of the batch save). */
  serverPoisRef: MutableRefObject<ITourPoi[]>
  /** Marks the saved selection as the new "clean" state. */
  commitPoisBaseline: (saved: ITourPoi[]) => void
}

/**
 * POI picker data and the selection of POIs on the tour.
 *
 * VI: Form tour chỉ lấy POI theo tenant + tenant_destination_id đã chọn để POI luôn nằm trong phạm vi đích đến của tenant. Mọi thay đổi POI (thêm/xoá/màu/hạng/thứ tự) chỉ cập nhật state cục bộ và được lưu một lần bằng nút "Lưu thay đổi POI". | EN: The tour form fetches POIs by tenant + the selected tenant_destination_id so the POIs stay inside the tenant destination scope. Every POI change (add/remove/colour/rank/order) only touches local state and is saved in one go by the "Save POI changes" button.
 */
export function useTourPois({
  tenantId,
  tenantDestinationId,
  languageCode,
  isEdit,
  tourId,
  selectedPois,
  updateSelectedPois,
  serverPoisRef,
  commitPoisBaseline,
}: UseTourPoisParams) {
  const { t } = useTranslation()
  const [queryFilters, setQueryFilters] = useState<PoiQueryFilters>({})

  const addTourPoisMutation = useAddTourPoisMutation()
  const removeTourPoisMutation = useRemoveTourPoisMutation()
  const updateTourPoiOrderMutation = useUpdateTourPoiOrderMutation()
  const updateTourPoisBatchMutation = useUpdateTourPoisBatchMutation()

  const canFetchPois = Boolean(tenantDestinationId && languageCode)
  const queryParams = useMemo(
    () => ({
      pageSize: TOUR_FORM_POI_PAGE_SIZE,
      ...queryFilters,
      ...(languageCode ? { languageCode } : {}),
      ...(tenantDestinationId ? { tenantDestinationId } : {}),
    }),
    [languageCode, queryFilters, tenantDestinationId],
  )
  const {
    data: poisInfiniteData,
    isLoading: isPoisLoading,
    isFetching: isPoisFetching,
    isFetchingNextPage: isPoisFetchingNextPage,
    hasNextPage: poisHasNextPage,
    fetchNextPage: fetchNextPoisPage,
  } = useTenantPoisInfiniteQuery(tenantId, queryParams, { enabled: canFetchPois })

  const pois = useMemo<IPOI[]>(
    () => poisInfiniteData?.pages.flatMap((page) => page.data) ?? [],
    [poisInfiniteData?.pages],
  )
  const poiMap = useMemo(() => buildPoiLookup(pois, selectedPois), [pois, selectedPois])
  const outOfScopePoiIds = useMemo(
    () => findOutOfScopePoiIds(selectedPois, poiMap, tenantDestinationId),
    [poiMap, selectedPois, tenantDestinationId],
  )

  const isPoiActionPending =
    addTourPoisMutation.isPending ||
    removeTourPoisMutation.isPending ||
    updateTourPoiOrderMutation.isPending ||
    updateTourPoisBatchMutation.isPending

  function addPoi(poi: IPOI) {
    updateSelectedPois((prev) => appendPois(prev, [poi]))
  }

  function addPoisMany(poisToAdd: IPOI[]) {
    updateSelectedPois((prev) => appendPois(prev, poisToAdd))
  }

  /**
   * Persists every pending POI change: removals, additions, colour/rank updates, then the final order.
   * Requests run in that order because the order endpoint needs the POIs to already exist on the tour.
   */
  async function handleSavePoisBatch() {
    if (!isEdit || !tourId) return

    const { toAdd, toRemove, toUpdate } = diffTourPois(serverPoisRef.current, selectedPois)

    try {
      if (toRemove.length > 0) {
        await removeTourPoisMutation.mutateAsync({
          id: tourId,
          poiIds: toRemove.map((poi) => poi.poiId),
        })
      }
      if (toAdd.length > 0) {
        await addTourPoisMutation.mutateAsync({ id: tourId, poiIds: toAdd.map((poi) => poi.poiId) })
      }
      if (toUpdate.length > 0) {
        await updateTourPoisBatchMutation.mutateAsync({ id: tourId, pois: toUpdate })
      }
      if (selectedPois.length > 0) {
        await updateTourPoiOrderMutation.mutateAsync({ id: tourId, pois: selectedPois })
      }

      commitPoisBaseline(selectedPois)
      showToast.success(t('common.saved', { defaultValue: 'Đã lưu.' }))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return {
    pois,
    poiMap,
    outOfScopePoiIds,
    isPoiActionPending,
    isSavingPoisBatch: updateTourPoisBatchMutation.isPending,
    isPoisLoading: Boolean(isPoisLoading),
    isPoisFetching: Boolean(isPoisFetching),
    isPoisFetchingNextPage,
    poisHasNextPage,
    fetchNextPoisPage,
    setQueryFilters,
    addPoi,
    addPoisMany,
    handleSavePoisBatch,
  }
}
